import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { canSeeRebates } from '@/core/rebateUtils';
import { MarketingSource } from '@/core/marketingSource';
import PostHogUtils from '@/core/PostHogUtils';
import * as Sentry from '@sentry/vue';

/**
 * Per-sport prop leagues (e.g. /baseball/mlb-props, /exchange/basketball/nba-props)
 * are no longer hosted on the legacy sportsbook/exchange views — they redirect
 * to the dedicated AllProps page with the sport chip pre-selected.
 *
 * This logic intentionally lives in the global `beforeEach` (further down in
 * this file) rather than per-route `beforeEnter`. Reason: vue-router only
 * fires `beforeEnter` when the matched *route* changes, not when params on
 * the same route change. Navigating from `/baseball/mlb` to `/baseball/mlb-props`
 * is a param-only change on the `GamesPage` route, so a `beforeEnter` guard
 * silently no-ops there. The global guard runs on every navigation.
 */
function isPropsLeagueRoute(to) {
  // Only the league-shaped routes carry a `:league` param. Bail early on
  // anything else so we don't run the regex over `/account/personal-info`
  // and friends every navigation.
  if (!to.params || !to.params.league) return false;
  return to.params.league.toString().toLowerCase().includes('props');
}

function propsRedirectTarget(to) {
  // Mirror the URL prefix the user is already on so the redirect doesn't
  // bounce them between sportsbook and exchange.
  const isExchange = (to.path || '').startsWith('/exchange/');
  const targetPath = isExchange ? '/exchange/all-props' : '/all-props';
  return { path: targetPath, query: { sport: to.params.sport } };
}

const routes = [
  {
    path: '/hot-reload-test',
    name: 'HotReloadTest',
    component: () => import('@/views/HotReloadTest.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'HomePage',
    component: () => import('@/views/nonBetting/Home/Index.vue'),
    meta: { requiresAuth: false },
    beforeEnter: async (to, from, next) => {
      const userStore = useUserStore();
      if (userStore.user.sportsbookDefault) {
        import('@/views/betting/Sportsbook/Index.vue');
      } else {
        import('@/views/betting/Exchange/Index.vue');
      }
      next();
    },
  },
  {
    // Legacy redirect — `/home` used to be the canonical homepage. Kept so
    // old bookmarks / external links keep working. Both vue-router and the
    // Caddy edge config (k8s/alpha/Caddyfile) should send these to `/`.
    path: '/home',
    redirect: '/',
  },
  {
    path: '/log-in',
    name: 'LoginPage',
    component: () => import('@/views/nonBetting/Login/Index.vue'),
    meta: {
      title: 'Login – 4casters Sports Betting Exchange',
      description:
        '4casters is a sports betting exchange. Trade peer-to-peer with better odds and no commission on offers.',
    },
  },
  {
    path: '/sign-up/:access?',
    name: 'SignUpPage',
    component: () => import('@/views/nonBetting/SignUp/Index.vue'),
    meta: {
      title: 'Sign Up – Join 4casters Sports Betting Exchange',
      description:
        '4casters is a sports betting exchange. Trade peer-to-peer with better odds and no commission on offers.',
      hideChrome: true,
    },
    // Theme override + IP-based geo check both live inside the SignUp component
    // (onMounted/onBeforeUnmount). Doing them here would block navigation on a
    // slow third-party fetch (~3s) and flash night mode on the previous page.
  },
  {
    // Discreet marketing-attribution links: 4casters.io/r/<code>. The code is
    // opaque on purpose (the meaning lives in a private marketing sheet, not the
    // URL) and is recorded first-touch, then the visitor is sent to the normal
    // homepage. Separate from /sign-up/<username> referral links so a marketing
    // code can never collide with a real referral username.
    path: '/r/:code',
    name: 'MarketingLink',
    redirect: (to) => {
      MarketingSource.recordCode(to.params.code);
      return { path: '/' };
    },
  },
  {
    path: '/not-available',
    name: 'GeoBlocked',
    component: () => import('@/views/nonBetting/GeoBlocked.vue'),
    meta: {
      title: 'Not Available – 4casters',
    },
  },
  // Signup2 shelved — component lives at views/nonBetting/SignUp2/Index.vue
  // {
  //   path: '/signup2',
  //   name: 'SignUp2Page',
  //   component: () => import('@/views/nonBetting/SignUp2/Index.vue'),
  //   meta: {
  //     title: 'Sign Up – Create Your Account',
  //     description:
  //       'Create an individual or entity account on the 4casters sports betting exchange.',
  //   },
  // },
  {
    path: '/account/personal-info',
    name: 'AccountPage',
    component: () => import('@/views/nonBetting/Account/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Cashier page removed — deposit/withdraw/transaction-history now live on the
    // My Account page. Redirect old links there instead of 404ing.
    path: '/cashier',
    redirect: '/account/personal-info',
  },
  {
    path: '/invite-friends',
    name: 'InviteFriends',
    component: () => import('@/views/specialUser/InviteFriends/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/affiliate-summary',
    name: 'AffiliateSummary',
    component: () => import('@/views/specialUser/AffiliateSummary/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/nonBetting/About/Index.vue'),
    meta: {
      title: 'Our Story – 4casters Professional Sports Betting Exchange',
      description:
        'Learn about 4casters, the sports betting exchange connecting bettors since 2021. Over $1 billion traded peer-to-peer.',
    },
  },
  {
    path: '/my-rebates',
    name: 'MyRebates',
    component: () => import('@/views/nonBetting/MyRebates/Index.vue'),
    meta: {
      requiresAuth: true,
      title: 'My Rebates – 4casters Sports Betting Exchange',
    },
    beforeEnter: (to, from, next) => {
      const userStore = useUserStore();
      if (canSeeRebates(userStore.user.username)) {
        next();
      } else {
        next({ path: '/home' });
      }
    },
  },
  {
    path: '/rules',
    name: 'Rules',
    component: () => import('@/views/nonBetting/Rules/Index.vue'),
    meta: {
      title: 'Betting Rules – 4casters Sports Betting Exchange Guidelines',
      description:
        '4casters is a sports betting exchange. Trade peer-to-peer with better odds and no commission on offers.',
    },
  },
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('@/views/nonBetting/Blog/Index.vue'),
    meta: {
      title: '4casters Blog – News and Updates',
      description:
        'News and updates from 4casters, the peer-to-peer sports betting exchange.',
    },
  },
  {
    path: '/blog/:slug',
    name: 'BlogPost',
    component: () => import('@/views/nonBetting/Blog/Post.vue'),
    meta: {
      isBlogPost: true,
    },
  },
  {
    path: '/all-props',
    name: 'AllProps',
    component: () => import('@/views/betting/AllProps/Index.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/exchange/all-props',
    name: 'AllPropsExchange',
    component: () => import('@/views/betting/AllProps/Index.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/sportsbook/all-props',
    name: 'AllPropsSportsbook',
    component: () => import('@/views/betting/AllProps/Index.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/exchange/:sport/:league',
    name: 'Exchange',
    component: () => import('@/views/betting/Exchange/Index.vue'),
    meta: {
      requiresAuth: false,
      title: '4C Exchange – Trade Sports',
      description:
        '4casters is a sports betting exchange. Trade peer-to-peer with better odds and no commission on offers.',
    },
  },
  {
    path: '/:sport/:league',
    name: 'GamesPage',
    component: () => import('@/views/betting/Sportsbook/Index.vue'),
    meta: {
      requiresAuth: false,
      isDynamicSportsPage: true,
    },
  },
  {
    path: '/exchange-single/:gameID',
    name: 'SingleGameMarket',
    component: () => import('@/views/betting/SingleGame/Index.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/my-bets/:type',
    name: 'MyBetsPage',
    component: () => import('@/views/nonBetting/ViewBets/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/combined-bets/pending',
    name: 'CombinedView',
    component: () =>
      import('@/views/nonBetting/ViewBets/CombinedBets/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/marketmaker-my-bets/:type',
    name: 'MMMyBets',
    component: () =>
      import('@/views/nonBetting/ViewBets/MarketMaker/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin-monkeymaker',
    name: 'MonkeyMachine',
    component: () => import('@/views/specialUser/Monkey/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/circle-game',
    name: 'CircleGame',
    component: () => import('@/views/specialUser/CircleGame/Index.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/service/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/service/ResetPassword.vue'),
    meta: { isService: true },
  },
  {
    path: '/service/confirm-email',
    name: 'ConfirmEmail',
    component: () => import('@/views/service/ConfirmEmail.vue'),
    meta: { isService: true },
  },
  {
    path: '/service/initiate-reset-password',
    name: 'InitiateResetPassword',
    component: () => import('@/views/service/InitiateResetPassword.vue'),
    meta: { isService: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
});

// Global guard for authentication and the props-league redirect.
router.beforeEach((to, from, next) => {
  // Run the props redirect first so we don't waste time on auth/canonical
  // bookkeeping for a navigation that's about to be replaced.
  if (isPropsLeagueRoute(to)) {
    next(propsRedirectTarget(to));
    return;
  }

  const userStore = useUserStore();

  // Update canonical URL and og:url
  const currentUrl = window.location.href;
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.href = currentUrl;
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.content = currentUrl;

  // SignUpPage manages its own theme override in onMounted/onBeforeUnmount so
  // the night-mode flash doesn't appear on the previous page during navigation.
  userStore.setTheme();
  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !userStore.user.isLoggedIn
  ) {
    next({ path: '/log-in' });
  } else if (to.path === '/log-in' && userStore.user.isLoggedIn) {
    next({ path: '/' });
  } else {
    next();
  }
});

const CHUNK_RELOAD_STORAGE_KEY = 'chunk-reload-attempt';

router.onError((error, to) => {
  Sentry.captureException(error);
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    const reloadKey = `${CHUNK_RELOAD_STORAGE_KEY}:${to.fullPath}`;
    if (sessionStorage.getItem(reloadKey)) {
      sessionStorage.removeItem(reloadKey);
      return;
    }

    sessionStorage.setItem(reloadKey, '1');
    window.location.href = to.fullPath;
  }
});

// ===========================================
// SEO META TAG SYSTEM
// ===========================================

const SPORTS_META = {
  'football/nfl': {
    title: 'NFL Betting Exchange | 4casters',
    description: 'Bet on NFL games peer-to-peer with better odds on 4casters.',
    h1: 'NFL Betting Exchange',
  },
  'football/nfl-preseason': {
    title: 'NFL Preseason Betting Exchange | 4casters',
    description: 'Bet on NFL preseason games peer-to-peer on 4casters.',
    h1: 'NFL Preseason Betting Exchange',
  },
  'football/1h-nfl': {
    title: 'NFL First Half Betting | 4casters',
    description: 'Trade NFL first half bets on 4casters.',
    h1: 'NFL First Half Betting',
  },
  'football/nfl-superbowl': {
    title: 'Super Bowl Betting Exchange | 4casters',
    description: 'Bet on the Super Bowl peer-to-peer on 4casters.',
    h1: 'Super Bowl Betting Exchange',
  },
  'football/ncaaf': {
    title: 'College Football Betting Exchange | 4casters',
    description: 'Trade NCAAF bets peer-to-peer on 4casters.',
    h1: 'College Football Betting Exchange',
  },
  'football/1h-ncaaf': {
    title: 'NCAAF First Half Betting | 4casters',
    description: 'Trade college football first half bets.',
    h1: 'NCAAF First Half Betting',
  },
  'basketball/nba': {
    title: 'NBA Betting Exchange | 4casters',
    description: 'NBA betting exchange for professional bettors.',
    h1: 'NBA Betting Exchange',
  },
  'basketball/1h-nba': {
    title: 'NBA First Half Betting | 4casters',
    description: 'Trade NBA first half bets on 4casters.',
    h1: 'NBA First Half Betting',
  },
  'basketball/wnba': {
    title: 'WNBA Betting Exchange | 4casters',
    description: 'WNBA betting exchange for professional bettors.',
    h1: 'WNBA Betting Exchange',
  },
  'basketball/ncaab': {
    title: 'College Basketball Betting Exchange | 4casters',
    description: 'Trade NCAAB bets on 4casters.',
    h1: 'College Basketball Betting Exchange',
  },
  'hockey/nhl': {
    title: 'NHL Betting Exchange | 4casters',
    description: 'NHL betting exchange for professional bettors.',
    h1: 'NHL Betting Exchange',
  },
  'hockey/1h-nhl': {
    title: 'NHL First Period Betting | 4casters',
    description: 'Trade NHL first period bets.',
    h1: 'NHL First Period Betting',
  },
  'fighting/all': {
    title: 'Combat Sports Betting Exchange | 4casters',
    description: 'Trade MMA and boxing bets on 4casters.',
    h1: 'Combat Sports Betting Exchange',
  },
  'golf/all': {
    title: 'PGA Golf Betting Exchange | 4casters',
    description: 'Trade golf bets on 4casters.',
    h1: 'PGA Golf Betting Exchange',
  },
  'tennis/atp': {
    title: 'ATP Tennis Betting Exchange | 4casters',
    description: "Trade men's tennis bets on 4casters.",
    h1: 'ATP Tennis Betting Exchange',
  },
  'tennis/wta': {
    title: 'WTA Tennis Betting Exchange | 4casters',
    description: "Trade women's tennis bets on 4casters.",
    h1: 'WTA Tennis Betting Exchange',
  },
  'soccer/featured-soccer': {
    title: 'Soccer Betting Exchange | 4casters',
    description: 'Trade soccer bets peer-to-peer.',
    h1: 'Soccer Betting Exchange',
  },
  'custom/custom': {
    title: 'Custom Betting Markets | 4casters',
    description: 'Create and trade custom betting markets.',
    h1: 'Custom Betting Markets',
  },
};

const DEFAULT_TITLE = '4casters – better betting';
const DEFAULT_DESCRIPTION =
  '4casters is a sports betting exchange. Trade peer-to-peer with better odds and no commission on offers.';

export function getSportsPageMeta(sport, league) {
  const key = `${sport}/${league}`;
  if (SPORTS_META[key]) {
    return SPORTS_META[key];
  }
  const formatted = league.toUpperCase().replace(/-/g, ' ');
  return {
    title: `${formatted} Betting Exchange | 4casters`,
    description: `Trade ${formatted} bets peer-to-peer on 4casters.`,
    h1: `${formatted} Betting Exchange`,
  };
}

export function updateMetaTag(name, content, attribute = 'name') {
  let tag = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content || '');
}

// Routes that should be noindex (auth pages that don't require login to view)
const NOINDEX_ROUTES = [
  '/log-in',
  '/sign-up',
  '/service/reset-password',
  '/service/confirm-email',
  '/service/initiate-reset-password',
];

router.afterEach((to) => {
  // SEO traffic measurement (PRO-118): landing attribution + content views.
  // Must run before the dynamic-sports-page branch below returns early —
  // sport pages are the bulk of the SEO content surface.
  PostHogUtils.trackContentView(to.path, useUserStore().user?.isLoggedIn === true);

  // Handle dynamic sports pages
  if (to.meta.isDynamicSportsPage && to.params.sport && to.params.league) {
    const meta = getSportsPageMeta(to.params.sport, to.params.league);
    document.title = meta.title;
    updateMetaTag('description', meta.description);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('og:title', meta.title, 'property');
    updateMetaTag('og:description', meta.description, 'property');
    // Store H1 for component
    to.meta.seoH1 = meta.h1;
    return;
  }

  // Blog posts own their meta — Post.vue sets title/description/og/JSON-LD
  // after the article fetch, so don't stomp them with defaults here.
  if (to.meta.isBlogPost) {
    updateMetaTag('robots', 'index, follow');
    return;
  }

  const title = to.meta.title || DEFAULT_TITLE;
  const description = to.meta.description || DEFAULT_DESCRIPTION;
  document.title = title;
  updateMetaTag('og:title', title, 'property');
  updateMetaTag('description', description);
  updateMetaTag('og:description', description, 'property');

  // Set robots: noindex for auth-related pages and pages requiring login
  const isNoindexRoute =
    to.meta.requiresAuth ||
    NOINDEX_ROUTES.some((route) => to.path.startsWith(route)) ||
    to.path.startsWith('/account') ||
    // The trading grid stays hidden from crawlers, but /exchange-single game
    // pages are public matchup pages and must stay indexable — the snapshot
    // layer (PRO-118) serves them to bots with "index, follow" and the SPA
    // must agree whenever a bot falls through to it.
    (to.path.startsWith('/exchange') && !to.path.startsWith('/exchange-single')) ||
    to.path.startsWith('/my-bets') ||
    to.path.startsWith('/combined-bets') ||
    to.path.startsWith('/marketmaker');

  if (isNoindexRoute) {
    updateMetaTag('robots', 'noindex, nofollow');
  } else {
    updateMetaTag('robots', 'index, follow');
  }
});

export default router;
